
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";
import { useEnhancedColorThemes } from "@/hooks/useEnhancedColorThemes";
import { getStorageUrl, STORAGE_BUCKETS } from "@/utils/storageUtils";

interface Character {
  id: string;
  name: string;
  species: string;
  role: string;
  description: string;
  personality: string;
  teaching_focus: string;
  special_talents: string;
  fun_facts: any;
  image_url: string;
  avatar_url: string;
  color_theme: string;
  age_group: string;
  featured: boolean;
}

interface CharacterHeroProps {
  character: Character;
}

export const CharacterHero = ({ character }: CharacterHeroProps) => {
  const { getEnhancedTheme } = useEnhancedColorThemes();
  
  const enhancedTheme = getEnhancedTheme(character.color_theme);

  // Get the image URL, handling both storage paths and external URLs
  const getImageUrl = (imagePath: string) => {
    if (!imagePath) return "";
    if (imagePath.startsWith('http')) return imagePath;
    return getStorageUrl(STORAGE_BUCKETS.CHARACTER_IMAGES, imagePath);
  };

  const avatarUrl = getImageUrl(character.avatar_url);
  const imageUrl = getImageUrl(character.image_url);

  return (
    <div 
      className="relative py-12 lg:py-16"
      style={{
        background: `linear-gradient(to right, ${enhancedTheme.gradientColors.to}, ${enhancedTheme.gradientColors.from})`
      }}
      role="banner" 
      aria-label={`${character.name} character hero section`}
    >
      {character.featured && (
        <Badge className="absolute top-6 right-6 bg-yellow-500 text-white z-10" aria-label="Featured character">
          <Star className="h-3 w-3 mr-1" aria-hidden="true" />
          Featured
        </Badge>
      )}
      
      <div className="absolute inset-0 bg-black/20" aria-hidden="true"></div>
      
      <div className="relative z-10 w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-center gap-8">
            <div className="w-48 h-48 lg:w-64 lg:h-64 rounded-full overflow-hidden shadow-2xl bg-white flex-shrink-0">
              {avatarUrl || imageUrl ? (
                <img
                  src={avatarUrl || imageUrl}
                  alt={`${character.name}, a ${character.species} character from Bobo & Teedee`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center" role="img" aria-label={`Placeholder image for ${character.name}`}>
                  <span className="text-6xl lg:text-8xl font-bold text-gray-600">
                    {character.name.charAt(0)}
                  </span>
                </div>
              )}
            </div>

            <div className="text-center lg:text-left text-white flex-1">
              <h1 className="text-4xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
                {character.name}
              </h1>
              
              <div className="flex flex-wrap justify-center lg:justify-start gap-3 mb-6" role="list" aria-label="Character attributes">
                {character.species && (
                  <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm" role="listitem">
                    {character.species}
                  </Badge>
                )}
                {character.role && (
                  <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm" role="listitem">
                    {character.role}
                  </Badge>
                )}
                {character.age_group && (
                  <Badge className="bg-yellow-500/90 text-white border-yellow-400" role="listitem">
                    Ages: {character.age_group}
                  </Badge>
                )}
              </div>

              {character.description && (
                <p className="text-lg lg:text-xl text-white/90 leading-relaxed max-w-3xl drop-shadow-sm">
                  {character.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
